import arcoAjuda from "./arco-ajuda.webp";
import arcoAmeaca from "./arco-ameaca.webp";
import { CARDS_COMUNS_CENTRO_OESTE, CARDS_RAROS_CENTRO_OESTE } from "./cartas";
import fundoColecao from "./colecao.webp";
import mascoteCabeca from "./mascote-cabeca.webp";
import mascoteCorpo from "./mascote-corpo.webp";
import fundoSelecao from "./selecao.webp";
import fundoDesfocado from "./tabuleiro-desfocado.webp";
import fundoTabuleiro from "./tabuleiro.webp";

// Região CENTRO-OESTE, bioma Cerrado, mascote Lobo-guará.
//
// O id é `centro-oeste`, e não `cerrado`: a região geográfica É a região do bioma, e
// há um nome só. "Cerrado" é o `bioma`, rótulo que o aluno lê.
//
// A PASTA DE ORIGEM DA ARTE CHAMA-SE `oeste`, e os dois nomes não vão convergir:
// `TemasDaTrilhas/oeste/` é como a arte chegou do ilustrador, e renomeá-la quebraria
// o rastro para o arquivo que ele tem; `centro-oeste` é o id, e já estava escrito no
// SVG do mapa (`#regiao-centro-oeste`) e em `LudicTrailSetting::REGIOES` no backend
// antes desta pasta existir. A divergência está registrada no catálogo do pipeline
// para não parecer engano.

export const MASCOTE_CENTRO_OESTE = {
  // O nome da ESPÉCIE, e não um apelido afetivo como o "Tatuzinho-bola" do Nordeste.
  // Está assim porque é como os 22 textos aprovados o chamam, do primeiro ao último:
  // adotar um apelido aqui obrigaria a reescrever os 22 para que o mascote não
  // tivesse dois nomes na mesma trilha. O MD registra a escolha do apelido como
  // pendência do cliente — quando ela vier, é aqui E lá.
  nome: "Lobo-guará",
  corpo: mascoteCorpo,
  cabeca: mascoteCabeca,
  // Ele fala no MASCULINO, ao contrário da Onça-pintada do Norte. É o que sustenta
  // "sozinho" na abertura e "orgulhoso" no encerramento.
  fala: "Oi, explorador(a)! Sou o Lobo-guará, o maior canídeo da América do Sul — e ando cada vez mais sozinho por aqui. Encara os desafios comigo e descubra como proteger o Cerrado. Bora?",
  // As duas do ENCERRAMENTO, com o álbum na tela.
  //
  // NÃO CONTAM AS CARTAS, e isso é regra e não estilo: numa trilha de 2 passos existe
  // uma única fase premiada, então o aluno termina com UMA carta — e essa é a trilha
  // mais curta que o professor pode montar. Qualquer frase que diga "essas cartas"
  // fica errada justamente ali. Quem conta é o cromo, acima do álbum.
  //
  // "Olha o que você descobriu" APONTA para o álbum logo abaixo em vez de descrevê-lo
  // — a imagem faz metade do trabalho, que é o recurso mais barato para quem está
  // aprendendo a ler. E o gênero vem num adjetivo sobre o PRÓPRIO lobo ("sossegado"),
  // não sobre o Cerrado: assim a concordância não sobrevive por acaso se alguém
  // reescrever a frase trocando o bioma.
  falaEncerramento:
    "Olha o que você descobriu no meu campo. Durmo mais sossegado sabendo que o Cerrado tem você por perto.",
  // O galho de álbum vazio — chegou ao fim sem carta nenhuma. Não menciona carta,
  // porque a tela não mostra nenhuma: celebra o percurso, que é real, e deixa uma
  // porta que também é verdadeira (refazer a trilha sorteia cartas novas do banco de
  // 20 comuns).
  falaEncerramentoSemCartas:
    "Você atravessou o meu Cerrado inteiro. Estou orgulhoso, e ainda tenho muita coisa pra te mostrar por aqui."
};

export const TABULEIRO_CENTRO_OESTE = {
  fundo: fundoTabuleiro,
  // Derivados os dois da segunda arte da pasta (`Bg_cerrado.png`, 2814x1536), e não
  // de três artes como nas regiões anteriores: esta região veio com duas. O
  // embaçado é o recorte quadrado (o `::before` o exibe em `cover` com `blur(12px)`)
  // e a seleção é a paisagem inteira, que é o que enche os cantos da tela deitada
  // atrás dos quatro exploradores. Ver `gerar_selecao` no pipeline para o que se
  // perde nisso — variedade, não resolução.
  fundoDesfocado,
  fundoSelecao,
  fundoColecao,
  proporcao: 2.2125,
  // Medido pelo método do CAMINHO, como o Norte e ao contrário da Caatinga: a vereda
  // é larga e serpenteia entre 38% e 68% da altura, com capim acima e abaixo, então a
  // média da massa cairia no meio de uma faixa que tem terra dos dois lados — e o
  // meio, nas curvas, é a ilha de capim que a vereda contorna.
  //
  // REMEDIDO em 20/08/2026, com a arte nova do ilustrador: 37,7% a 67,9% da altura;
  // salto na emenda 1,7pp; pior par de vizinhas 4,3pp; as 192 posições caem sobre o
  // leito, na primeira medição e sem calibragem à mão. Conferido no desenho
  // (`ver-perfil-da-estrada.py`) e no navegador (`tc-pista-horizontal.js` +
  // `conferir-nos-na-estrada.py`: 4 de 4 nós no leito, erro de 0,1pp entre a altura
  // declarada aqui e a renderizada).
  //
  // ⚠️ O PERFIL É DA ARTE, e trocar a arte invalida o perfil sem avisar. O anterior
  // (35,2% a 66,5%, da arte de 17/08) reprovava 1 das 192 posições na arte nova — a
  // amostra 22, que caía na borda entre leito e campo. Uma reprovação em 192 não
  // aparece na tela como erro: aparece como um nó pousado no capim, e só se vê
  // olhando. Ao substituir arte, rode `gerar-perfil-estrada.py` mesmo que a cena
  // pareça a mesma.
  perfilDaEstrada: [
    47.6, 46.7, 46.6, 46.8, 47.2, 48.8, 49.6, 49.6, 50.6, 53.3, 55.7, 58.9,
    62.8, 65.8, 67.0, 67.9, 67.6, 65.8, 63.3, 60.5, 57.1, 52.8, 49.0, 45.0,
    41.8, 39.0, 37.7, 38.0, 39.4, 41.2, 43.6, 46.2, 47.4, 48.6, 49.1, 49.2,
    49.5, 49.8, 50.0, 50.1, 50.0, 49.9, 50.0, 50.0, 50.2, 50.4, 50.1, 49.3
  ]
};

export const CORES_CENTRO_OESTE = {
  // O accent é o LILÁS DO IPÊ-ROXO, e aqui ele NÃO sai da arte do mascote — que é a
  // regra que o Norte seguiu, e seguir cegamente daria errado. Lá a onça é dourada
  // sobre uma floresta verde, então a cor dela contrasta com a cena por construção.
  // Aqui o lobo é ruivo (#e43c24, medido na arte) sobre um Cerrado que é laranja de
  // ponta a ponta: a cor do mascote É a cor do cenário, e um accent tirado dela
  // desapareceria contra o próprio fundo.
  //
  // O ipê-roxo resolve por ser o oposto exato do que a cena tem (roxo e laranja são
  // complementares) e continuar sendo do bioma — é a flor que abre no Cerrado seco,
  // não uma cor inventada para contrastar.
  region: "#7c5cd6",
  regionStrong: "#4c357f",
  // O degradê da casca, claro → médio → escuro, com as cores do campo: palha, capim
  // dourado e a terra vermelha na sombra. A base fica em 25,1% de claridade, que é o
  // valor das DUAS regiões já aprovadas (25% na Caatinga e no Norte) — não é
  // coincidência que batam: é a parte de baixo da casca, e ela precisa fechar o
  // cenário sem virar um segundo tabuleiro.
  fundoTopo: "#f6ead0",
  fundoMeio: "#cfa964",
  fundoBase: "#62341e",
  // O véu sobre a ilustração embaçada do `::before`: as três de cima com alfa, nas
  // mesmas opacidades da Caatinga — que é a região de paleta mais próxima desta.
  veuTopo: "rgba(246, 234, 208, 0.5)",
  veuMeio: "rgba(207, 169, 100, 0.3)",
  veuBase: "rgba(98, 52, 30, 0.5)",
  // O véu radial da escolha do explorador, na paleta desta região e não na de
  // nenhuma outra: foi faltar isto que fez a seleção do Norte aparecer com o deserto
  // da Caatinga em 05/08/2026.
  veuSelecaoCentro: "rgba(246, 234, 208, 0.45)",
  veuSelecaoMeio: "rgba(207, 169, 100, 0.22)",
  veuSelecaoBorda: "rgba(98, 52, 30, 0.1)",
  // O cromo (setas, pílula do contador) CONVERGE com o accent aqui, e no Norte
  // diverge — o que não é inconsistência, é a mesma regra dando resultados
  // diferentes. A regra é: o cromo aparece numa pílula BRANCA sobre o cenário, e não
  // pode ser confundido com o dourado de PRÊMIO das estrelas dos nós. O ouro da onça
  // era exatamente esse dourado, então o Norte precisou de uma segunda cor; o lilás
  // do ipê não se parece com prêmio nenhum, então serve nos dois lugares.
  //
  // Sobre branco dá 6,1:1 na borda e 9,9:1 no texto — folgado para o chevron da seta
  // e para "Fase 3 de 8", e acima do que as duas regiões existentes entregam
  // (3,4:1 e 6,4:1 no Norte; 1,7:1 e 3,2:1 na Caatinga).
  chromeBorda: "#6d4bc4",
  chromeTexto: "#4c357f",
  chromeFundo: "#efe9fb"
};

// A FIGURA DO ARCO, na ordem de `cardsRaros` — ameaça primeiro, fecho por último.
//
// É a cena que o aluno vê no fim de leva quando a carta rara NÃO é sorteada (ver
// `cenaDaFase` em ../../../nucleo/recompensaDaFase.js): o mascote cabisbaixo na
// ameaça, de braços erguidos na ajuda, e nenhum prêmio.
//
// SÓ A ARTE, e é o ponto. Nome e fala do arco não estão aqui porque já existem: são
// os da carta que teria caído ali. Uma segunda cópia do texto no descritor
// divergiria da primeira no dia em que alguém ajustasse uma pílula em
// `TemasDaTrilhas/`, que é a fonte de verdade e de onde `gerar-cartas-trilha.py`
// gera `cartas.js`.
//
// LISTA E NÃO `{ ameaca, ajuda }`: quem casa fase rara com momento do arco é o
// alinhamento pelo fim, que trabalha com ÍNDICE. Com um objeto de dois nomes, o
// núcleo teria de saber que o índice 0 se chama "ameaca" — ou seja conheceria a
// narrativa da região, que é exatamente o que ele não faz.
//
// Derivada por `ferramentas-da-trilha/gerar-figuras-trilha.py` (modo `arco`) da arte
// da carta rara: fora a moldura de madeira, e a borda dissolvida.
export const ARCO_CENTRO_OESTE = [arcoAmeaca, arcoAjuda];

export const REGIAO_CENTRO_OESTE = {
  id: "centro-oeste",
  nome: "Centro-Oeste",
  bioma: "Cerrado",
  mascote: MASCOTE_CENTRO_OESTE,
  cardsComuns: CARDS_COMUNS_CENTRO_OESTE,
  cardsRaros: CARDS_RAROS_CENTRO_OESTE,
  arco: ARCO_CENTRO_OESTE,
  tabuleiro: TABULEIRO_CENTRO_OESTE,
  cores: CORES_CENTRO_OESTE
};

export {
  CARDS_COMUNS_CENTRO_OESTE,
  CARDS_RAROS_CENTRO_OESTE,
  CARDS_CENTRO_OESTE
} from "./cartas";
